/**
 * Step 2 of the CDMX-20060 template chain: a CMX note type (step 1, ./note-type-resolution) ->
 * the template that orders that note's sections, and the arrangement of a note's sliced sections
 * against it.
 *
 * Templates are the configuration-service value `cmx_document_content_note_templates`, read through
 * the resolved endpoint so tenant and service-line overrides apply server side. The value is
 * generated from product's note-type sheet by configuration-service's
 * scripts/cmx-document-content-note-templates, which refuses any section code outside the spec's
 * section-code registry (CMXDC-ANN-029) -- so a template code and an L5 `section_code` are spelled
 * from the same list and an exact comparison is the right one.
 */

import type { SectionSlice } from './section-slicer';

export const NOTE_TEMPLATES_CONFIGURATION_KEY =
  'cmx_document_content_note_templates';

export type SectionCriticality = 'Critical' | 'Standard';

export type TemplateSection = {
  sectionCode: string;
  criticality: SectionCriticality;
};

export type NoteTemplate = {
  /** Product's note type label, e.g. "History and Physical". */
  label: string;
  /** Product's template id, e.g. "FR-23". */
  templateKey: string;
  /** Display order. */
  sections: TemplateSection[];
};

/** The configuration value. */
export type NoteTemplatesConfig = {
  specVersion: string;
  /**
   * CMX Note Type label (the "Note Type" dictionary's `CMX Note Type` column, verbatim) -> note
   * type key. A label with no entry has no template yet; a note type with no label reaching it
   * is in `templates` but unreachable until product adds one.
   */
  noteTypes: Record<string, string>;
  /** Note type key -> template. */
  templates: Record<string, NoteTemplate>;
  /** Section code -> display label, from the section-code registry. */
  sectionLabels: Record<string, string>;
};

export type ResolvedNoteTemplate = {
  cmxNoteType: string;
  noteTypeKey: string;
  template: NoteTemplate;
  sectionLabels: Record<string, string>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isTemplate(value: unknown): value is NoteTemplate {
  return (
    isRecord(value) &&
    typeof value.label === 'string' &&
    typeof value.templateKey === 'string' &&
    Array.isArray(value.sections) &&
    value.sections.every(
      (s) => isRecord(s) && typeof s.sectionCode === 'string'
    )
  );
}

/**
 * The `jsonValue` off a configuration read, or `null` if it is not a usable value.
 *
 * Defensive rather than trusting: the value is editable per tenant, and a malformed override must
 * degrade to note order, never take the viewer down. Accepts a string because `jsonValue` is an
 * `Object` on the DTO and has been seen serialised both ways.
 */
export function parseNoteTemplatesConfig(
  jsonValue: unknown
): NoteTemplatesConfig | null {
  let value = jsonValue;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (
    !isRecord(value) ||
    !isRecord(value.noteTypes) ||
    !isRecord(value.templates)
  ) {
    return null;
  }
  const templates: Record<string, NoteTemplate> = {};
  for (const [key, template] of Object.entries(value.templates)) {
    if (isTemplate(template)) {
      templates[key] = template;
    }
  }
  const noteTypes: Record<string, string> = {};
  for (const [label, key] of Object.entries(value.noteTypes)) {
    if (typeof key === 'string') {
      noteTypes[label] = key;
    }
  }
  const sectionLabels: Record<string, string> = {};
  if (isRecord(value.sectionLabels)) {
    for (const [code, label] of Object.entries(value.sectionLabels)) {
      if (typeof label === 'string') {
        sectionLabels[code] = label;
      }
    }
  }
  return {
    specVersion: typeof value.specVersion === 'string' ? value.specVersion : '',
    noteTypes,
    templates,
    sectionLabels
  };
}

/**
 * The template for a CMX note type, or `null`. The label is matched exactly: both sides are
 * copied from the same dictionary column, so a case or spacing difference is a configuration
 * error worth seeing as "no template" rather than papering over.
 */
export function selectNoteTemplate(
  config: NoteTemplatesConfig | null | undefined,
  cmxNoteType: string | null | undefined
): ResolvedNoteTemplate | null {
  if (!config || !cmxNoteType) {
    return null;
  }
  const noteTypeKey = config.noteTypes[cmxNoteType];
  const template = noteTypeKey ? config.templates[noteTypeKey] : undefined;
  if (!noteTypeKey || !template) {
    return null;
  }
  return {
    cmxNoteType,
    noteTypeKey,
    template,
    sectionLabels: config.sectionLabels
  };
}

export type PlacedTemplateSection = {
  section: TemplateSection;
  label: string;
  /** The note's sections carrying this code, in note order. Empty: not identified. */
  slices: SectionSlice[];
};

export type TemplateArrangement = {
  placed: PlacedTemplateSection[];
  /**
   * Sections the template does not place, in note order: a code the template does not list, or
   * no code at all (CMXDC-ANN-018 makes an untyped section a complete assertion).
   */
  unplaced: SectionSlice[];
};

/**
 * Lays the note's sections out in template order.
 *
 * Several slices can share a code -- one section split around another (`sameInstance`), or two
 * genuinely separate sections of one kind, such as two attestations -- and all of them go under
 * the code's entry in note order; telling those two cases apart is not needed to place them.
 *
 * A template entry with no slice is NOT "missing". The spec has no way to assert absence (§8), and
 * an untyped section may well be it, so the only honest statement is that this note's sections
 * did not identify it.
 */
export function arrangeByTemplate(
  slices: readonly SectionSlice[],
  resolved: ResolvedNoteTemplate
): TemplateArrangement {
  const listed = new Set(resolved.template.sections.map((s) => s.sectionCode));
  const placed = resolved.template.sections.map((section) => ({
    section,
    label: resolved.sectionLabels[section.sectionCode] ?? section.sectionCode,
    slices: slices.filter((slice) => slice.sectionCode === section.sectionCode)
  }));
  const unplaced = slices.filter(
    (slice) => slice.sectionCode === null || !listed.has(slice.sectionCode)
  );
  return { placed, unplaced };
}
